import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import {
    Box,
    Button,
    FormLabel,
    HStack,
    IconButton,
    Input,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useDisclosure,
    useToast,
    VStack,
} from "@chakra-ui/react";
import FormModal from "../../../components/FormModal";
import { WhatsAppLink } from "models/organization/dto/organization.dto";
import { useEffect, useRef, useState } from "react";
import PageCard from "../../../components/page-card";
import { translate } from "../../../i18n";
import { useSelectedOrganization } from "../hooks/useSelectedOrganization";
import { useUpdateWhatsAppLinks } from "../hooks/useUpdateWhatsAppLinks";

export default function WhatsAppLinksManager() {
    const { selectedOrganization } = useSelectedOrganization();
    const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [links, setLinks] = useState<WhatsAppLink[]>([]);
    const [formData, setFormData] = useState<WhatsAppLink>({ title: '', description: '', url: '' });
    const previousOrgIdRef = useRef<string | undefined>(undefined);
    const isUpdatingRef = useRef(false);

    const { mutateAsync: updateWhatsAppLinks, isLoading: isUpdating } =
        useUpdateWhatsAppLinks(selectedOrganization?.id || '', (updatedData) => {
            // Update local state with server response after successful mutation
            if (updatedData?.whatsappLinks) {
                setLinks([...updatedData.whatsappLinks]);
            }
            isUpdatingRef.current = false;
            onClose();
            resetForm();
        });

    useEffect(() => {
        // Only sync from selectedOrganization when the organization ID changes
        // This prevents clearing the list during optimistic updates
        const orgIdChanged = previousOrgIdRef.current !== selectedOrganization?.id;

        if (orgIdChanged) {
            previousOrgIdRef.current = selectedOrganization?.id;
            // When organization changes, sync the links
            if (selectedOrganization?.whatsappLinks) {
                setLinks([...selectedOrganization.whatsappLinks]);
            } else {
                setLinks([]);
            }
        }
    }, [selectedOrganization?.id]);

    const resetForm = () => {
        setFormData({ title: '', description: '', url: '' });
        setIsEditMode(false);
        setEditingIndex(null);
    };

    const handleOpenAdd = () => {
        resetForm();
        onOpen();
    };

    const handleOpenEdit = (index: number) => {
        setFormData(links[index]);
        setIsEditMode(true);
        setEditingIndex(index);
        onOpen();
    };

    const handleDelete = async (index: number) => {
        const previousLinks = [...links];
        const newLinks = links.filter((_, i) => i !== index);
        // Optimistic update - update local state immediately
        setLinks(newLinks);
        isUpdatingRef.current = true;
        try {
            await updateWhatsAppLinks({ whatsappLinks: newLinks });
        } catch {
            // Revert to pre-mutation snapshot, not live selectedOrganization
            isUpdatingRef.current = false;
            setLinks(previousLinks);
            toast({ title: translate('common.error'), status: 'error', duration: 3000 });
        }
    };

    const handleSave = async () => {
        if (!formData.title || !formData.url) {
            return;
        }

        const newLinks = [...links];
        if (isEditMode && editingIndex !== null) {
            newLinks[editingIndex] = formData;
        } else {
            newLinks.push(formData);
        }

        // Optimistic update - update local state immediately
        const previousLinks = [...links];
        setLinks(newLinks);
        isUpdatingRef.current = true;
        try {
            await updateWhatsAppLinks({ whatsappLinks: newLinks });
        } catch {
            // Revert to pre-mutation snapshot, not live selectedOrganization
            isUpdatingRef.current = false;
            setLinks(previousLinks);
            toast({ title: translate('common.error'), status: 'error', duration: 3000 });
        }
    };

    const handleClose = () => {
        onClose();
        resetForm();
    };

    if (!selectedOrganization) return null;

    return (
        <>
            <PageCard
                title={translate('organization.whatsapp_links')}
            >
                <Box p={4}>
                    <Text mb={4} color={textColorPrimary} fontSize="sm">
                        {translate('organization.whatsapp_links_description')}
                    </Text>

                    {links.length === 0 ? (
                        <Box textAlign="center" py={8} color="gray.500">
                            <Text mb={4}>{translate('organization.no_links_configured')}</Text>
                            <Button
                                leftIcon={<AddIcon />}
                                colorScheme="brand"
                                onClick={handleOpenAdd}
                            >
                                {translate('organization.add_whatsapp_link')}
                            </Button>
                        </Box>
                    ) : (
                        <VStack spacing={4} align="stretch">
                            <TableContainer>
                                <Table variant="simple" size="sm">
                                    <Thead>
                                        <Tr>
                                            <Th>{translate('organization.table_title')}</Th>
                                            <Th>{translate('organization.table_description')}</Th>
                                            <Th>{translate('organization.table_url')}</Th>
                                            <Th>{translate('organization.table_actions')}</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {links.map((link, index) => (
                                            <Tr key={index}>
                                                <Td>{link.title}</Td>
                                                <Td>{link.description}</Td>
                                                <Td>
                                                    <Text isTruncated maxW="200px">
                                                        {link.url}
                                                    </Text>
                                                </Td>
                                                <Td>
                                                    <HStack spacing={2}>
                                                        <IconButton
                                                            aria-label={translate('common.edit')}
                                                            icon={<EditIcon />}
                                                            size="sm"
                                                            onClick={() => handleOpenEdit(index)}
                                                        />
                                                        <IconButton
                                                            aria-label={translate('common.delete')}
                                                            icon={<DeleteIcon />}
                                                            size="sm"
                                                            colorScheme="red"
                                                            onClick={() => handleDelete(index)}
                                                        />
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                            <Button
                                leftIcon={<AddIcon />}
                                colorScheme="brand"
                                onClick={handleOpenAdd}
                            >
                                {translate('organization.add_whatsapp_link')}
                            </Button>
                        </VStack>
                    )}
                </Box>
            </PageCard>

            <FormModal
                isOpen={isOpen}
                onClose={handleClose}
                size="lg"
                title={isEditMode
                    ? translate('organization.edit_whatsapp_link')
                    : translate('organization.add_whatsapp_link')
                }
                onSubmit={handleSave}
                submitLabel={translate('common.save')}
                cancelLabel={translate('common.cancel')}
                isSubmitting={isUpdating}
                isSubmitDisabled={!formData.title || !formData.url}
            >
                        <VStack spacing={4} align="stretch">
                            <Box>
                                <FormLabel>{translate('organization.link_title')}</FormLabel>
                                <Input
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="e.g., Level 1"
                                />
                            </Box>

                            <Box>
                                <FormLabel>{translate('organization.link_description')}</FormLabel>
                                <Input
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="e.g., Strictly for Level 1"
                                />
                            </Box>

                            <Box>
                                <FormLabel>{translate('organization.link_url')}</FormLabel>
                                <Input
                                    value={formData.url}
                                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                                    placeholder="https://chat.whatsapp.com/..."
                                    type="url"
                                />
                            </Box>
                        </VStack>
            </FormModal>
        </>
    );
}
